import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import memoize from 'memoize-one';

import messages, { labels } from './messages';

import process from './utils';

import MainView from './components/MainView';
import DetailedView from './components/DetailedView';
import { useSelector } from 'react-redux';
import session from 'containers/App/session';

const Container = styled.div`
    width: 100%;
`;
const MessageContainer = styled.div`
    padding: 15px;
`;

const BusinessAnalysisContainer = ({ filters, data = null, hideFooter = false, hideOccupancy = false }) => {
    const intl = useIntl();

    const { startDate, endDate } = filters;

    const currency = useSelector(session.selectors.selectCurrency);

    const [selectedSegment, setSelectedSegment] = useState(null);

    /**
     * Process the data & decorate it with translated title
     * Function is memoized to avoid unnecessary computation when the data does not change
     * @param data - from props
     * @return {Object} object with keys for each segment
     */
    const processData = memoize((dataToProcess) =>
        Object.entries(
            process(
                dataToProcess,
                intl.formatMessage(messages.missingLabel),
                intl.formatMessage(messages.othersLabel),
                intl.formatMessage(messages.totalsLabel),
            ),
        ).reduce(
            (accu, [key, object]) =>
                labels[key] === undefined
                    ? accu
                    : { ...accu, [key]: { ...object, key, title: intl.formatMessage(labels[key]) } },
            {},
        ),
    );

    const onOpenDetailedView = (segment) => {
        setSelectedSegment(selectedSegment === segment ? null : segment);
    };

    const onCloseDetailedView = () => {
        setSelectedSegment(null);
    };

    const renderNoDataMessage = () => (
        <MessageContainer>
            <FormattedMessage {...messages.noData} />
        </MessageContainer>
    );

    const renderMainView = (processedData) => (
        <MainView
            segments={Object.values(processedData).filter((elt) => elt)}
            openDetailedView={onOpenDetailedView}
            selectedSegment={selectedSegment}
        />
    );

    const renderDetailedView = (processedData) => {
        const segment = processedData[selectedSegment];
        if (segment === undefined) return null;
        const { title } = segment;

        return (
            <DetailedView
                {...segment}
                currency={currency}
                closeDetailedView={onCloseDetailedView}
                hideFooter={hideFooter}
                hideOccupancy={hideOccupancy}
                exportedFileName={`${startDate}_${endDate}__BusinessAnalysis_${title}.csv`}
            />
        );
    };

    if (!(data && Object.values(data).find((row) => row !== null))) {
        return renderNoDataMessage();
    }

    const segments = processData(data);
    return (
        <Container>
            {renderMainView(segments)}
            {renderDetailedView(segments)}
        </Container>
    );
};

export default BusinessAnalysisContainer;
